/**  MODIFIED COPY OF src/common-app/state/modules/engines/voice-mask/reducers.ts TODO find way to reuse common code! */
import { Lens } from 'monocle-ts';

import * as Actions from './actions';
import {
  defaultState,
  defaultJobSlice,
  FAILED_STATUSES,
  JobState,
  JobStore,
} from './store';
import { TDOId } from '../../universal/models/Brands';
import { createReducer } from '@reduxjs/toolkit';
import { GetActionCreatorPayloadT } from '@utils';

const engineGroup = (tdoId: TDOId) =>
  Lens.fromNullableProp<JobStore>()(tdoId, {});
const engineJob = (jobId: string) =>
  Lens.fromNullableProp<NonNullable<JobStore['']>>()(jobId, defaultJobSlice());
const getJob = (tdoId: TDOId, jobId: string) =>
  engineGroup(tdoId).compose(engineJob(jobId)).get;
const modifyJob = (
  tdoId: TDOId,
  jobId: string,
  fn: (j: JobState) => JobState
) => engineGroup(tdoId).compose(engineJob(jobId)).modify(fn);

const processEngineRequestSuccess = (
  state: JobStore,
  { payload }: { payload: { tdoId: TDOId; jobId: string } }
) =>
  modifyJob(payload.tdoId, payload.jobId, (job) => ({
    ...job,
    isRunning: false,
    endedOn: new Date(),
    error: undefined,
  }))(state);

const createJobSuccess = (
  state: JobStore,
  {
    payload,
  }: {
    payload: GetActionCreatorPayloadT<typeof Actions.CREATE_ENGINE_JOB_SUCCESS>;
  }
) =>
  modifyJob(payload.createJob.targetId, payload.createJob.id, (_job) => ({
    jobId: payload.createJob.id,
    isRunning: true,
    status: 'pending',
    startedOn: new Date(),
    endedOn: undefined,
    error: undefined,
  }))(state);

const startPollingEngineResults = (
  state: JobStore,
  { payload }: { payload: { tdoId: TDOId; jobId: string } }
) =>
  modifyJob(payload.tdoId, payload.jobId, (job) => ({
    ...job,
    jobId: payload.jobId,
    isRunning: true,
    status: 'pending',
    error: undefined,
  }))(state);

const stopPollingEngineResults = (
  state: JobStore,
  { payload }: { payload: { tdoId: TDOId; jobId: string } }
) =>
  modifyJob(payload.tdoId, payload.jobId, (job) => ({
    ...job,
    isRunning: false,
  }))(state);

/**
 * Record a status against the run's job slice. Shared by the job and task
 * status replies, which differ only in where the status was read from: a run
 * started here owns its whole job, while one picked up from the upload DAG
 * shares its job with every other upload engine and must be read off its own
 * task (VE-30784).
 */
const applyStatus = (
  state: JobStore,
  tdoId: TDOId,
  jobId: string,
  status: JobState['status'],
  describe: string
) => {
  if (status === getJob(tdoId, jobId)(state).status) {
    return state;
  }

  const isFailed = FAILED_STATUSES.includes(status);

  return modifyJob(tdoId, jobId, (job) => ({
    ...job,
    status,
    isRunning: isFailed ? false : job.isRunning,
    endedOn: isFailed ? new Date() : job.endedOn,
    error: isFailed ? `Speaker separation ${describe} ${status}` : job.error,
  }))(state);
};

const checkJobStatusSuccess = (
  state: JobStore,
  {
    payload,
  }: {
    payload: {
      job: { targetId: TDOId; id: string; status: JobState['status'] };
    };
  }
) =>
  applyStatus(
    state,
    payload.job.targetId,
    payload.job.id,
    payload.job.status,
    'job'
  );

const checkTaskStatusSuccess = (
  state: JobStore,
  {
    payload,
  }: {
    payload: {
      task: {
        targetId: TDOId;
        id: string;
        jobId: string;
        status: JobState['status'];
      };
    };
  }
) =>
  // keyed by jobId: the poller's slice is the job's, the status is the task's
  applyStatus(
    state,
    payload.task.targetId,
    payload.task.jobId,
    payload.task.status,
    'task'
  );

export default createReducer(defaultState, (builder) => {
  builder
    .addCase(
      Actions.PROCESS_ENGINE_REQUEST_SUCCESS,
      processEngineRequestSuccess
    )
    .addCase(Actions.CREATE_ENGINE_JOB_SUCCESS, createJobSuccess)
    .addCase(Actions.START_POLLING_ENGINE_RESULTS, startPollingEngineResults)
    .addCase(Actions.STOP_POLLING_ENGINE_RESULTS, stopPollingEngineResults)
    .addCase(Actions.CHECK_JOB_STATUS_SUCCESS, checkJobStatusSuccess)
    .addCase(Actions.CHECK_TASK_STATUS_SUCCESS, checkTaskStatusSuccess);
});
